import { Box, Card, styled, Tabs, Tab } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { useEffect, useState, useCallback } from "react";
import SelfRegisteredTable from "page-sections/Self-registered-members/SelfRegisteredTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";

const StyledTab = styled(Tab)(({ theme }) => ({
    fontSize: 13,
    minHeight: 40,
    fontWeight: 600,
    minWidth: 'auto',
    marginRight: 12,
    paddingLeft: 8,
    paddingRight: 8,
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
        color: theme.palette.primary.main
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: 11,
        marginRight: 8,
        paddingLeft: 4,
        paddingRight: 4,
    }
}));

const SelfRegisteredMembers = () => {
    const { setPageTitle } = usePageTitle();
    const [members, setMembers] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [currentTab, setCurrentTab] = useState(1); // 1 for pending, 2 for accepted, 3 for rejected

    useEffect(() => {
        setPageTitle('Self-Registered Members');
    }, [setPageTitle]);

    const fetchMembers = useCallback(async () => {
        try {
            const userId = JSON.parse(localStorage.getItem("logged_data"))?.user?.id;
            let params = `user_id=${userId}`;
            
            // Add status parameter based on current tab
            if (currentTab === 1) {
                params += '&accepted=false&is_rejected=false';
            } else if (currentTab === 2) {
                params += '&accepted=true';
            } else if (currentTab === 3) {
                params += '&accepted=rejected';
            }

            const response = await apiService.get(
                `${API_ENDPOINTS.GET_self_registered_members}?${params}`
            );

            if (response?.success && response?.data) {
                setMembers(response.data);
            }
        } catch (error) {
            console.error("API error:", error);
            setMembers([]);
        }
    }, [currentTab]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const handleTabChange = (_, newValue) => {
        setCurrentTab(newValue);
    };

    return (
        <Box pt={2} pb={4}>
            <FlexBetween flexWrap="wrap" mb={2}>
                <FlexBox alignItems="center" gap={2}>
                    <SearchInput
                        bordered={false}
                        placeholder="Search Member"
                        onChange={e => setSearchValue(e.target.value)}
                        value={searchValue}
                    />
                </FlexBox>
            </FlexBetween>

            <Card>
                <Box sx={{ 
                    borderBottom: 1, 
                    borderColor: 'divider', 
                    px: { xs: 1, sm: 2, md: 3 },
                    pt: 1 
                }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                    >
                        <StyledTab 
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span>Pending</span>
                                    {members.length > 0 && currentTab === 1 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'primary.contrastText',
                                                borderRadius: '50%',
                                                width: 20,
                                                height: 20,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {members.length}
                                        </Box>
                                    )}
                                </Box>
                            }
                            value={1}
                        />
                        <StyledTab label="Accepted" value={2} />
                        <StyledTab label="Rejected" value={3} />
                    </Tabs>
                </Box>

                <Box sx={{ mt: 1 }}>
                    <SelfRegisteredTable
                        members={members}
                        searchQuery={searchValue}
                        onMemberStatusChange={fetchMembers}
                        currentTab={currentTab}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default SelfRegisteredMembers; 